import Chart from 'chart.js/auto';
import {
    buildFilterSelects,
    attachTimeListeners,
    attachFilterListeners,
    getVisibleCombos,
    getActiveSegmentKey,
    normalizeData,
    hoverLinePlugin,
} from './mmi-chart-shared.js';

(function () {
    const panel = document.querySelector('[data-mmi-investment-chart]');
    if (!panel) return;

    const canvas = panel.querySelector('canvas');
    const timeButtons = panel.querySelectorAll('[data-time-filter]');

    let rawData = [];
    let rowMap = {};
    let activeTime = '2yr';
    let activeFilters = {
        region: '',
        band: '',
        industry: '',
        ownershipType: '',
    };
    let chartInstance = null;

    // ── Data processing ──────────────────────────────────────────────────────

    function computeChartData() {
        const { visibleCombos } = getVisibleCombos(rawData, activeTime);
        const segmentKey = getActiveSegmentKey(activeFilters);
        const baseKey = segmentKey || 'National';

        const labels = visibleCombos.map(k => { const [y, q] = k.split('|'); return `${y} Q${q}`; });

        const investValues = visibleCombos.map(k => {
            const row = rowMap[k];
            if (!row) return null;
            const v = parseFloat(row[`${baseKey}:Invest`]);
            return isNaN(v) ? null : v;
        });

        const holdValues = visibleCombos.map(k => {
            const row = rowMap[k];
            if (!row) return null;
            const v = parseFloat(row[`${baseKey}:Hold`]);
            return isNaN(v) ? null : v;
        });

        return { labels, investValues, holdValues };
    }

    // ── Chart rendering ──────────────────────────────────────────────────────

    const COLORS = {
        invest: '#00AF95',
        hold: '#004769',
    };

    function makeDataset(label, data, color) {
        return {
            label,
            data,
            borderColor: color,
            backgroundColor: 'transparent',
            pointBackgroundColor: color,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: false,
            tension: 0.3,
            borderWidth: 2,
            spanGaps: true,
        };
    }

    function renderChart() {
        const { labels, investValues, holdValues } = computeChartData();

        if (chartInstance) {
            chartInstance.data.labels = labels;
            chartInstance.data.datasets[0].data = investValues;
            chartInstance.data.datasets[1].data = holdValues;
            chartInstance.update();
            return;
        }

        chartInstance = new Chart(canvas, {
            type: 'line',
            plugins: [hoverLinePlugin],
            data: {
                labels,
                datasets: [
                    makeDataset('Invest (%)', investValues, COLORS.invest),
                    makeDataset('Hold or Cash (%)', holdValues, COLORS.hold),
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                animation: { duration: 500, easing: 'easeInOutQuart' },
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                        align: 'start',
                        title: {
                            display: true,
                            text: ' ',
                            padding: { bottom: 40 },
                        },
                        labels: {
                            usePointStyle: true,
                            pointStyle: 'circle',
                            boxHeight: 8,
                            padding: 20,
                        },
                    },
                    tooltip: {
                        backgroundColor: '#ffffff',
                        borderColor: '#082B41',
                        borderWidth: 1,
                        padding: 20,
                        usePointStyle: true,
                        titleColor: '#082B41',
                        bodyColor: '#082B41',
                        footerColor: '#082B41',
                        callbacks: {
                            labelPointStyle: () => ({
                                pointStyle: 'circle',
                                rotation: 0,
                            }),
                            label: ctx => {
                                const v = ctx.parsed.y;
                                return v !== null
                                    ? `${ctx.dataset.label.replace(' (%)', '')}: ${v}%`
                                    : `${ctx.dataset.label.replace(' (%)', '')}: No data`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: {
                            autoSkip: true,
                            maxRotation: 0,
                            minRotation: 0,
                            font: { size: 11 },
                        },
                    },
                    y: {
                        border: { display: false },
                        grid: {
                            color: 'rgba(0,0,0,0.07)',
                        },
                        ticks: {
                            callback: v => `${v}%`,
                            font: { size: 11 },
                        },
                    },
                },
            },
        });
    }

    // ── Inline data ──────────────────────────────────────────────────────────

    const dataScript = document.getElementById('mmi-data-investment');
    if (!dataScript) return;
    try {
        rawData = normalizeData(JSON.parse(dataScript.textContent));
    } catch (err) {
        console.error('[mmi-investment-chart] Failed to parse inline data:', err);
        return;
    }
    if (!rawData.length) return;
    rowMap = Object.fromEntries(rawData.map(d => [`${d.Year}|${d.Quarter}`, d]));
    buildFilterSelects(panel, rawData);
    attachTimeListeners(timeButtons, time => { activeTime = time; renderChart(); });
    attachFilterListeners(panel, activeFilters, renderChart);
    renderChart();
})();
